import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Inventory2 as Inventory2Icon,
  LocalShipping as LocalShippingIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

import StockTransferService from "../../services/StockTransferService";


const PAGE_SIZE = 50;


/**
 * ============================================================
 * UNIFIED DISPATCH / RETURN INBOUND PAGE
 * ============================================================
 *
 * This is ONE page for:
 *
 * factorymanager
 * devicemanufacture
 * dealer
 *
 * IMPORTANT:
 * ------------------------------------------------------------
 * We DO NOT send the logged-in user's role to the API.
 *
 * Backend resolves:
 *
 * factorymanager    -> factory
 * devicemanufacture -> manufacturer
 * dealer            -> dealer
 *
 * The inbound endpoint automatically returns only registers
 * that were dispatched TO the authenticated caller.
 *
 * ============================================================
 */
const DispatchInbound = () => {

  /**
   * ==========================================================
   * STATE
   * ==========================================================
   */

  const [registers, setRegisters] = useState([]);

  const [loading, setLoading] = useState(false);

  const [processing, setProcessing] = useState(false);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const [expandedRegister, setExpandedRegister] =
    useState(null);

  /**
   * Selected decisions:
   *
   * {
   *   [itemId]: {
   *      decision: "accept" | "reject",
   *      reason: ""
   *   }
   * }
   */
  const [decisions, setDecisions] = useState({});

  /**
   * Reject dialog
   */
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    item: null,
  });

  const [rejectReason, setRejectReason] =
    useState("");

  /**
   * Result dialog
   */
  const [resultDialog, setResultDialog] = useState({
    open: false,
    results: [],
  });

  /**
   * Snackbar
   */
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });


  /**
   * ==========================================================
   * SNACKBAR
   * ==========================================================
   */

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };


  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };


  /**
   * ==========================================================
   * ERROR MESSAGE
   * ==========================================================
   */

  const getErrorMessage = (error) => {

    return (
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong"
    );
  };


  /**
   * ==========================================================
   * LOAD INBOUND REGISTERS
   * ==========================================================
   *
   * POST
   *
   * /api/stock/transfer/inbound/
   *
   * IMPORTANT:
   *
   * We DON'T send:
   *
   * role
   * user_type
   * party_type
   * user_id
   *
   * Backend identifies the caller from authentication.
   */
  const loadInboundRegisters = async () => {

    setLoading(true);

    try {

      const response =
        await StockTransferService.getInboundTransfers({
          page,
          page_size: PAGE_SIZE,
        });


      const data =
        response?.data || {};


      const results =
        Array.isArray(data.results)
          ? data.results
          : [];


      setRegisters(results);

      setTotal(
        Number(data.total || 0)
      );


      /**
       * Reset selections because the list
       * has been refreshed.
       */
      setDecisions({});


    } catch (error) {

      console.error(
        "Inbound register error:",
        error
      );

      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoading(false);

    }
  };


  /**
   * ==========================================================
   * INITIAL LOAD
   * ==========================================================
   */

  useEffect(() => {

    loadInboundRegisters();

  }, [page]);


  /**
   * ==========================================================
   * TOTAL PENDING ITEMS
   * ==========================================================
   */

  const totalPendingItems = useMemo(() => {

    return registers.reduce(
      (totalCount, register) =>
        totalCount +
        Number(
          register.pending_count || 0
        ),
      0
    );

  }, [registers]);


  /**
   * ==========================================================
   * TOTAL ACCEPTED
   * ==========================================================
   */

  const totalAcceptedItems = useMemo(() => {

    return registers.reduce(
      (totalCount, register) =>
        totalCount +
        Number(
          register.accepted_count || 0
        ),
      0
    );

  }, [registers]);


  /**
   * ==========================================================
   * TOTAL REJECTED
   * ==========================================================
   */

  const totalRejectedItems = useMemo(() => {

    return registers.reduce(
      (totalCount, register) =>
        totalCount +
        Number(
          register.rejected_count || 0
        ),
      0
    );

  }, [registers]);


  /**
   * ==========================================================
   * FORMAT DATE
   * ==========================================================
   */

  const formatDate = (date) => {

    if (!date) {
      return "-";
    }

    try {

      return new Date(date).toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );

    } catch {

      return date;

    }
  };


  /**
   * ==========================================================
   * SET ACCEPT DECISION
   * ==========================================================
   */

  const handleAcceptItem = (item) => {

    if (!item?.item_id) {
      return;
    }

    setDecisions((prev) => ({
      ...prev,

      [item.item_id]: {
        decision: "accept",
        reason: "",
      },
    }));

  };


  /**
   * ==========================================================
   * OPEN REJECT DIALOG
   * ==========================================================
   */

  // const handleOpenReject = (item) => {

  //   if (!item?.item_id) {
  //     return;
  //   }

  //   setRejectReason("");

  //   setRejectDialog({
  //     open: true,
  //     item,
  //   });

  // };


  /**
   * ==========================================================
   * CLOSE REJECT DIALOG
   * ==========================================================
   */

  // const handleCloseReject = () => {

  //   setRejectDialog({
  //     open: false,
  //     item: null,
  //   });

  //   setRejectReason("");

  // };


  /**
   * ==========================================================
   * CONFIRM REJECT
   * ==========================================================
   */

  // const handleConfirmReject = () => {

  //   const item =
  //     rejectDialog.item;


  //   if (!item?.item_id) {
  //     return;
  //   }


  //   setDecisions((prev) => ({
  //     ...prev,

  //     [item.item_id]: {
  //       decision: "reject",
  //       reason:
  //         rejectReason.trim(),
  //     },
  //   }));


  //   handleCloseReject();

  // };


  /**
   * ==========================================================
   * CLEAR DECISION
   * ==========================================================
   */

  const clearDecision = (itemId) => {

    setDecisions((prev) => {

      const next = {
        ...prev,
      };

      delete next[itemId];

      return next;

    });

  };


  /**
   * ==========================================================
   * ACCEPT ALL PENDING ITEMS IN REGISTER
   * ==========================================================
   */

  const handleAcceptAll = (register) => {

    const items =
      Array.isArray(register?.items)
        ? register.items
        : [];


    const pendingItems =
      items.filter(
        (item) =>
          item.status ===
          "pending"
      );


    if (!pendingItems.length) {

      showSnackbar(
        "No In-Transit items available",
        "info"
      );

      return;
    }


    setDecisions((prev) => {

      const next = {
        ...prev,
      };


      pendingItems.forEach(
        (item) => {

          next[item.item_id] = {
            decision: "accept",
            reason: "",
          };

        }
      );


      return next;

    });


    showSnackbar(
      `${pendingItems.length} item(s) marked for acceptance`,
      "success"
    );

  };


  /**
   * ==========================================================
   * CLEAR ALL DECISIONS IN REGISTER
   * ==========================================================
   */

  const handleClearAll = (register) => {

    const items =
      Array.isArray(register?.items)
        ? register.items
        : [];


    const itemIds =
      items.map(
        (item) =>
          item.item_id
      );


    setDecisions((prev) => {

      const next = {
        ...prev,
      };


      itemIds.forEach(
        (id) => {

          delete next[id];

        }
      );


      return next;

    });

  };


  /**
   * ==========================================================
   * SUBMIT SELECTED DECISIONS
   * ==========================================================
   *
   * POST
   *
   * /api/stock/transfer/receive/
   *
   * Payload:
   *
   * {
   *   decisions: [
   *     {
   *       item_id: 101,
   *       decision: "accept"
   *     },
   *     {
   *       item_id: 102,
   *       decision: "reject",
   *       reason: "Wrong item"
   *     }
   *   ]
   * }
   *
   * Backend determines whether caller is:
   *
   * factory
   * manufacturer
   * dealer
   *
   * and verifies that the item belongs to an inbound
   * register dispatched TO that caller.
   */
  const handleSubmitDecisions = async () => {

    const selectedItems =
      Object.entries(
        decisions
      );


    if (!selectedItems.length) {

      showSnackbar(
        "Please select at least one accept or reject action",
        "warning"
      );

      return;
    }


    /**
     * Build API decisions.
     */
    const payloadDecisions =
      selectedItems.map(
        ([itemId, decisionData]) => {

          // const item = {
          //   item_id:
          //     Number(itemId),
          //   decision:
          //     decisionData.decision,
          // };

          const item = Object.entries(decisions).map(([itemId]) => ({
  item_id: Number(itemId),
  decision: "accept",
}));

          /**
           * Reason is required/useful for reject.
           */
          // if (
          //   decisionData.decision ===
          //     "reject" &&
          //   decisionData.reason
          // ) {

          //   item.reason =
          //     decisionData.reason;

          // }


          return item;

        }
      );


    /**
     * Prevent reject without reason.
     */
    // const invalidReject =
    //   payloadDecisions.find(
    //     (item) =>
    //       item.decision ===
    //         "reject" &&
    //       !item.reason
    //   );


    // if (invalidReject) {

    //   showSnackbar(
    //     "Please provide a reason for every rejected item",
    //     "warning"
    //   );

    //   return;
    // }


    setProcessing(true);


    try {

      const response =
        await StockTransferService.receiveTransfer({
          decisions:
            payloadDecisions,
        });


      const data =
        response?.data || {};


      const results =
        Array.isArray(data.results)
          ? data.results
          : [];


      /**
       * Show API results.
       */
      setResultDialog({
        open: true,
        results,
      });


      /**
       * Count result status.
       */
      const successCount =
        results.filter(
          (item) =>
            item.status ===
            "success"
        ).length;


      const errorCount =
        results.filter(
          (item) =>
            item.status ===
            "error"
        ).length;


      if (
        successCount > 0 &&
        errorCount > 0
      ) {

        showSnackbar(
          `${successCount} action(s) completed, ${errorCount} failed`,
          "warning"
        );

      } else if (
        successCount > 0
      ) {

        showSnackbar(
          `${successCount} action(s) completed successfully`,
          "success"
        );

      } else {

        showSnackbar(
          "No action was processed",
          "error"
        );

      }


      /**
       * Reload from backend.
       *
       * This is important because accepted/rejected
       * items should no longer remain pending.
       */
      await loadInboundRegisters();


    } catch (error) {

      console.error(
        "Receive transfer error:",
        error
      );


      showSnackbar(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setProcessing(false);

    }

  };


  /**
   * ==========================================================
   * EXPAND REGISTER
   * ==========================================================
   */

  const toggleRegister = (registerId) => {

    setExpandedRegister(
      (current) =>
        current === registerId
          ? null
          : registerId
    );

  };


  /**
   * ==========================================================
   * GET REGISTER DECISION COUNT
   * ==========================================================
   */

  const getRegisterDecisionCount = (
    register
  ) => {

    const items =
      Array.isArray(register?.items)
        ? register.items
        : [];


    const pendingIds =
      items
        .filter(
          (item) =>
            item.status ===
            "pending"
        )
        .map(
          (item) =>
            item.item_id
        );


    return pendingIds.filter(
      (id) =>
        decisions[id]
    ).length;

  };


  /**
   * ==========================================================
   * RENDER ITEM STATUS
   * ==========================================================
   */

  const renderStatusChip = (
    status
  ) => {

    if (status === "pending") {

      return (
        <Chip
          label="In-Transit"
          size="small"
          color="warning"
          variant="outlined"
        />
      );

    }


    if (status === "accepted") {

      return (
        <Chip
          label="Received"
          size="small"
          color="success"
        />
      );

    }


    // if (status === "rejected") {

    //   return (
    //     <Chip
    //       label="Rejected"
    //       size="small"
    //       color="error"
    //     />
    //   );

    // }


    return (
      <Chip
        label={
          status || "Unknown"
        }
        size="small"
        variant="outlined"
      />
    );

  };


  /**
   * ==========================================================
   * RENDER
   * ==========================================================
   */

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
        background:
          "#f8fafc",
        minHeight:
          "100vh",
      }}
    >

      {/* ======================================================
          HEADER
      ====================================================== */}

      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          border:
            "1px solid #e2e8f0",
          boxShadow:
            "0 4px 20px rgba(15,23,42,0.05)",
        }}
      >

        <CardContent>

          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            justifyContent="space-between"
            alignItems={{
              xs: "flex-start",
              md: "center",
            }}
            spacing={2}
          >

            <Box>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
              >

                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg,#0ea5e9,#14b8a6)",
                    color: "#fff",
                  }}
                >

                  <Inventory2Icon />

                </Box>


                <Box>

                  <Typography
                    variant="h5"
                    fontWeight={800}
                    color="#0f172a"
                  >
                    Dispatch Inbound
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Review incoming device
                    transfers and accept individual items.
                  </Typography>

                </Box>

              </Stack>

            </Box>


            <Button
              variant="outlined"
              startIcon={
                loading ? (
                  <CircularProgress
                    size={18}
                  />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={
                loadInboundRegisters
              }
              disabled={
                loading ||
                processing
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Refresh
            </Button>

          </Stack>

        </CardContent>

      </Card>


      {/* ======================================================
          SUMMARY
      ====================================================== */}

      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >

          <SummaryCard
            title="Registers"
            value={registers.length}
            subtitle={`Total ${total}`}
            icon={
              <LocalShippingIcon />
            }
          />

        </Grid>


        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >

          <SummaryCard
            title="In-Transit Items"
            value={totalPendingItems}
            subtitle="Awaiting In-Transit"
            icon={
              <Inventory2Icon />
            }
          />

        </Grid>


        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >

          <SummaryCard
            title="Received"
            value={totalAcceptedItems}
            subtitle="Already Received"
            icon={
              <CheckCircleIcon />
            }
          />

        </Grid>


        {/* <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >

          <SummaryCard
            title="Rejected"
            value={totalRejectedItems}
            subtitle="Already rejected"
            icon={
              <CancelIcon />
            }
          />

        </Grid> */}

      </Grid>


      {/* ======================================================
          LOADING
      ====================================================== */}

      {loading && !registers.length && (

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 8,
          }}
        >

          <CircularProgress />

        </Box>

      )}


      {/* ======================================================
          EMPTY STATE
      ====================================================== */}

      {!loading &&
        registers.length === 0 && (

          <Paper
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 3,
              border:
                "1px solid #e2e8f0",
            }}
          >

            <Inventory2Icon
              sx={{
                fontSize: 56,
                color:
                  "text.disabled",
                mb: 1,
              }}
            />

            <Typography
              variant="h6"
              fontWeight={700}
            >
              No inbound transfers
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              There are currently no
              dispatch registers waiting
              for your action.
            </Typography>

          </Paper>

        )}


      {/* ======================================================
          REGISTERS
      ====================================================== */}

      <Stack spacing={2}>

        {registers.map(
          (register) => {

            const isExpanded =
              expandedRegister ===
              register.register_id;


            const decisionCount =
              getRegisterDecisionCount(
                register
              );


            const items =
              Array.isArray(
                register.items
              )
                ? register.items
                : [];


            const pendingItems =
              items.filter(
                (item) =>
                  item.status ===
                  "pending"
              );


            return (

              <Card
                key={
                  register.register_id
                }
                sx={{
                  borderRadius: 3,
                  border:
                    "1px solid #e2e8f0",
                  boxShadow:
                    "0 3px 15px rgba(15,23,42,0.04)",
                  overflow: "hidden",
                }}
              >

                {/* =================================================
                    REGISTER HEADER
                ================================================= */}

                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    cursor: "pointer",
                    "&:hover": {
                      background:
                        "#f8fafc",
                    },
                  }}
                  onClick={() =>
                    toggleRegister(
                      register.register_id
                    )
                  }
                >

                  <Stack
                    direction={{
                      xs: "column",
                      md: "row",
                    }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "flex-start",
                      md: "center",
                    }}
                  >

                    <Box>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >

                        <Typography
                          fontWeight={800}
                          color="#0f172a"
                        >
                          Register #
                          {
                            register.register_id
                          }
                        </Typography>


                        <Chip
                          label={
                            register.direction ||
                            "Transfer"
                          }
                          size="small"
                          color="primary"
                          variant="outlined"
                        />

                      </Stack>


                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={{
                          xs: 0.5,
                          sm: 2,
                        }}
                        sx={{
                          mt: 1,
                        }}
                      >

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          From:{" "}
                          <strong>
                            {
                              register.from
                                ?.name ||
                              "-"
                            }
                          </strong>
                        </Typography>


                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          To:{" "}
                          <strong>
                            {
                              register.to
                                ?.name ||
                              "-"
                            }
                          </strong>
                        </Typography>


                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Date:{" "}
                          <strong>
                            {formatDate(
                              register.dispatch_date
                            )}
                          </strong>
                        </Typography>

                      </Stack>

                    </Box>


                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >

                      <Chip
                        label={`${register.pending_count || 0} In-Transit`}
                        color="warning"
                        size="small"
                      />

                      <Chip
                        label={`${register.accepted_count || 0} Received`}
                        color="success"
                        size="small"
                      />

                      {/* <Chip
                        label={`${register.rejected_count || 0} Rejected`}
                        color="error"
                        size="small"
                      /> */}

                      <IconButton
                        sx={{
                          transform:
                            isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          transition:
                            "transform 0.2s",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>

                    </Stack>

                  </Stack>

                </Box>


                {/* =================================================
                    REGISTER DETAILS
                ================================================= */}

                <Collapse
                  in={isExpanded}
                  timeout="auto"
                  unmountOnExit
                >

                  <Divider />


                  <CardContent>

                    {/* ============================================
                        SHIPPING DETAILS
                    ============================================ */}

                    <Grid
                      container
                      spacing={2}
                      sx={{
                        mb: 2,
                      }}
                    >

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >

                        <DetailField
                          label="Tracking No."
                          value={
                            register.tracking_no ||
                            "-"
                          }
                        />

                      </Grid>


                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >

                        <DetailField
                          label="Dispatch Mode"
                          value={
                            register.dispatch_mode ||
                            "-"
                          }
                        />

                      </Grid>


                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >

                        <DetailField
                          label="Dispatch Date"
                          value={
                            formatDate(
                              register.dispatch_date
                            )
                          }
                        />

                      </Grid>


                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >

                        <DetailField
                          label="Total Items"
                          value={
                            register.item_count ||
                            items.length
                          }
                        />

                      </Grid>


                      {register.comments && (

                        <Grid
                          item
                          xs={12}
                        >

                          <DetailField
                            label="Comments"
                            value={
                              register.comments
                            }
                          />

                        </Grid>

                      )}

                    </Grid>


                    {/* ============================================
                        ACTION BAR
                    ============================================ */}

                    {pendingItems.length > 0 && (

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: 2,
                          background:
                            "#f8fafc",
                        }}
                      >

                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          justifyContent="space-between"
                          alignItems={{
                            xs: "flex-start",
                            sm: "center",
                          }}
                          spacing={1.5}
                        >

                          <Box>

                            <Typography
                              fontWeight={700}
                            >
                              In-Transit items
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {
                                decisionCount
                              } of{" "}
                              {
                                pendingItems.length
                              } item(s) selected
                              for action.
                            </Typography>

                          </Box>


                          <Stack
                            direction="row"
                            spacing={1}
                          >

                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={
                                <CheckCircleIcon />
                              }
                              onClick={() =>
                                handleAcceptAll(
                                  register
                                )
                              }
                            >
                              Accept All
                            </Button>


                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                handleClearAll(
                                  register
                                )
                              }
                            >
                              Clear
                            </Button>

                          </Stack>

                        </Stack>

                      </Paper>

                    )}


                    {/* ============================================
                        ITEM LIST
                    ============================================ */}

                    <Stack spacing={1.5}>

                      {items.map(
                        (item) => {

                          const itemDecision =
                            decisions[
                              item.item_id
                            ];


                          const isPending =
                            item.status ===
                            "pending";


                          return (

                            <Paper
                              key={
                                item.item_id
                              }
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderColor:
                                  itemDecision
                                    ?.decision ===
                                  "accept"
                                    ? "#86efac"
                                    : itemDecision
                                        ?.decision ===
                                      "reject"
                                    ? "#fca5a5"
                                    : "#e2e8f0",
                                background:
                                  itemDecision
                                    ?.decision ===
                                  "accept"
                                    ? "#f0fdf4"
                                    : itemDecision
                                        ?.decision ===
                                      "reject"
                                    ? "#fef2f2"
                                    : "#fff",
                              }}
                            >

                              <Stack
                                direction={{
                                  xs: "column",
                                  md: "row",
                                }}
                                spacing={2}
                                alignItems={{
                                  xs: "flex-start",
                                  md: "center",
                                }}
                                justifyContent="space-between"
                              >

                                {/* DEVICE INFORMATION */}

                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >

                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                  >

                                    <Typography
                                      fontWeight={800}
                                    >
                                      Item #
                                      {
                                        item.item_id
                                      }
                                    </Typography>

                                    {
                                      renderStatusChip(
                                        item.status
                                      )
                                    }

                                  </Stack>


                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      mt: 0.5,
                                      wordBreak:
                                        "break-all",
                                    }}
                                  >
                                    IMEI:{" "}
                                    <strong>
                                      {
                                        item.imei ||
                                        "-"
                                      }
                                    </strong>
                                  </Typography>


                                  {itemDecision
                                    ?.decision ===
                                    "reject" &&
                                    itemDecision
                                      ?.reason && (

                                      <Typography
                                        variant="caption"
                                        color="error"
                                        display="block"
                                        sx={{
                                          mt: 0.5,
                                        }}
                                      >
                                        Reject reason:{" "}
                                        {
                                          itemDecision.reason
                                        }
                                      </Typography>

                                    )}

                                </Box>


                                {/* ACTIONS */}

                                {isPending && (

                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexShrink={0}
                                  >

                                    <Button
                                      variant={
                                        itemDecision
                                          ?.decision ===
                                        "accept"
                                          ? "contained"
                                          : "outlined"
                                      }
                                      color="success"
                                      startIcon={
                                        <CheckCircleIcon />
                                      }
                                      onClick={() =>
                                        handleAcceptItem(
                                          item
                                        )
                                      }
                                      sx={{
                                        textTransform:
                                          "none",
                                        fontWeight: 700,
                                        borderRadius: 2,
                                      }}
                                    >
                                      Accept
                                    </Button>


                                    {/* <Button
                                      variant={
                                        itemDecision
                                          ?.decision ===
                                        "reject"
                                          ? "contained"
                                          : "outlined"
                                      }
                                      color="error"
                                      startIcon={
                                        <CancelIcon />
                                      }
                                      onClick={() =>
                                        handleOpenReject(
                                          item
                                        )
                                      }
                                      sx={{
                                        textTransform:
                                          "none",
                                        fontWeight: 700,
                                        borderRadius: 2,
                                      }}
                                    >
                                      Reject
                                    </Button> */}

                                  </Stack>

                                )}

                              </Stack>

                            </Paper>

                          );

                        }
                      )}

                    </Stack>


                    {/* ============================================
                        SUBMIT DECISIONS
                    ============================================ */}

                    {pendingItems.length > 0 && (

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >

                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          disabled={
                            processing ||
                            decisionCount === 0
                          }
                          onClick={
                            handleSubmitDecisions
                          }
                          startIcon={
                            processing ? (
                              <CircularProgress
                                size={20}
                                color="inherit"
                              />
                            ) : (
                              <CheckCircleIcon />
                            )
                          }
                          sx={{
                            borderRadius: 2,
                            textTransform:
                              "none",
                            fontWeight: 800,
                            px: 3,
                          }}
                        >

                          {processing
                            ? "Processing..."
                            : `Submit ${decisionCount} Decision${
                                decisionCount === 1
                                  ? ""
                                  : "s"
                              }`}

                        </Button>

                      </Box>

                    )}

                  </CardContent>

                </Collapse>

              </Card>

            );

          }
        )}

      </Stack>


      {/* ======================================================
          PAGINATION
      ====================================================== */}

      {total > PAGE_SIZE && (

        <Stack
          direction="row"
          justifyContent="center"
          spacing={2}
          sx={{
            mt: 3,
          }}
        >

          <Button
            variant="outlined"
            disabled={
              page <= 1 ||
              loading
            }
            onClick={() =>
              setPage(
                (prev) =>
                  Math.max(
                    1,
                    prev - 1
                  )
              )
            }
          >
            Previous
          </Button>


          <Chip
            label={`Page ${page}`}
          />


          <Button
            variant="outlined"
            disabled={
              page * PAGE_SIZE >=
                total ||
              loading
            }
            onClick={() =>
              setPage(
                (prev) =>
                  prev + 1
              )
            }
          >
            Next
          </Button>

        </Stack>

      )}


      {/* ======================================================
          REJECT REASON DIALOG
      ====================================================== */}

      {/* <Dialog
        open={
          rejectDialog.open
        }
        onClose={
          handleCloseReject
        }
        maxWidth="sm"
        fullWidth
      >

        <DialogTitle
          sx={{
            fontWeight: 800,
          }}
        >
          Reject Device
        </DialogTitle>


        <DialogContent>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
            }}
          >
            Provide a reason for rejecting
            this device.
          </Typography>


          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reject Reason"
            value={
              rejectReason
            }
            onChange={(e) =>
              setRejectReason(
                e.target.value
              )
            }
            placeholder="Enter rejection reason"
            autoFocus
          />

        </DialogContent>


        <DialogActions
          sx={{
            p: 2,
          }}
        >

          <Button
            onClick={
              handleCloseReject
            }
            sx={{
              textTransform:
                "none",
            }}
          >
            Cancel
          </Button>


          <Button
            variant="contained"
            color="error"
            disabled={
              !rejectReason.trim()
            }
            onClick={
              handleConfirmReject
            }
            sx={{
              textTransform:
                "none",
                fontWeight: 700,
            }}
          >
            Confirm Reject
          </Button>

        </DialogActions>

      </Dialog> */}


      {/* ======================================================
          RESULT DIALOG
      ====================================================== */}

      <Dialog
        open={
          resultDialog.open
        }
        onClose={() =>
          setResultDialog({
            open: false,
            results: [],
          })
        }
        maxWidth="md"
        fullWidth
      >

        <DialogTitle
          sx={{
            fontWeight: 800,
          }}
        >
          Processing Result
        </DialogTitle>


        <DialogContent>

          <Stack spacing={1.5}>

            {resultDialog.results.map(
              (result, index) => (

                <Paper
                  key={
                    result.item_id ||
                    index
                  }
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                  }}
                >

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >

                    <Box>

                      <Typography
                        fontWeight={700}
                      >
                        Item #
                        {
                          result.item_id ||
                          result.id ||
                          "-"
                        }
                      </Typography>

                      {result.device_stock_id && (

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Device Stock ID:{" "}
                          {
                            result.device_stock_id
                          }
                        </Typography>

                      )}

                    </Box>


                    <Chip
                      label={
                        result.status
                      }
                      color={
                        result.status ===
                        "success"
                          ? "success"
                          : "error"
                      }
                    />

                  </Stack>


                  {result.error && (

                    <Typography
                      variant="body2"
                      color="error"
                      sx={{
                        mt: 1,
                      }}
                    >
                      {
                        result.error
                      }
                    </Typography>

                  )}

                </Paper>

              )
            )}

          </Stack>

        </DialogContent>


        <DialogActions>

          <Button
            onClick={() =>
              setResultDialog({
                open: false,
                results: [],
              })
            }
            sx={{
              textTransform:
                "none",
            }}
          >
            Close
          </Button>

        </DialogActions>

      </Dialog>


      {/* ======================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={4000}
        onClose={
          closeSnackbar
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={
            closeSnackbar
          }
          sx={{
            width: "100%",
          }}
        >
          {
            snackbar.message
          }
        </Alert>

      </Snackbar>

    </Box>
  );
};


/**
 * ============================================================
 * SUMMARY CARD
 * ============================================================
 */

const SummaryCard = ({
  title,
  value,
  subtitle,
  icon,
}) => {

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border:
          "1px solid #e2e8f0",
        boxShadow:
          "0 3px 15px rgba(15,23,42,0.04)",
      }}
    >

      <CardContent>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >

          <Box>

            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
            >
              {title}
            </Typography>


            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mt: 0.5,
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="caption"
              color="text.secondary"
            >
              {subtitle}
            </Typography>

          </Box>


          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "#eff6ff",
              color:
                "#0284c7",
            }}
          >
            {icon}
          </Box>

        </Stack>

      </CardContent>

    </Card>
  );
};


/**
 * ============================================================
 * DETAIL FIELD
 * ============================================================
 */

const DetailField = ({
  label,
  value,
}) => {

  return (
    <Box>

      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={700}
        sx={{
          mt: 0.25,
          wordBreak:
            "break-word",
        }}
      >
        {value}
      </Typography>

    </Box>
  );
};


export default DispatchInbound;